import moment from 'moment';
import { faker } from '@faker-js/faker';
import { User } from './users';
import { Product } from './products';
import { Auth } from '../common/auth';
import { API_TIMEOUT } from '../../config/app';
import { catalog, objectId } from './catalog';
import usersJsonSeed from '../../assets/json/users.json';

const allUsers = User.all();
const allProducts = Product.all();
const localUsersEmails = usersJsonSeed.map((user) => user.email);
const customers = allUsers.filter((user) => user.role === 'user');
const localUsers = allUsers.filter((user) => localUsersEmails.includes(user.email));

const arrayLoop = (length) => {
    return Array.from(Array(length).keys());
};

const random = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min);
};

const randomItem = (array) => {
    return array[Math.floor(Math.random() * array.length)];
};

const randomOrderItems = () => {
    return arrayLoop(random(1, 10)).map(() => {
        const product = randomItem(allProducts);
        const quantity = random(1, 5);
        return {
            id: objectId(),
            product: product.id,
            price: product.price,
            quantity
        };
    });
};

const randomOrder = (initial = {}) => {
    const { date: initDate, user: initUser, status: initStatus, items: initItems } = initial;
    const user = initUser || randomItem(customers);
    const status = initStatus || randomItem(['Open', 'Paid', 'Closed', 'Cancelled']);
    const date = initDate || moment(faker.date.past()).format('YYYY/MM/DD HH:mm:ss');
    const items = initItems || randomOrderItems();
    return {
        id: objectId(),
        user: user.id,
        status,
        items,
        date
    };
};

const randomUsersOrders = arrayLoop(100).map(() => {
    return randomOrder();
});

const localUsersOrders = localUsers.reduce((orders, user) => {
    return orders.concat(arrayLoop(5).map(() => randomOrder({ user })));
}, []);

const testUsersOrders = localUsers.reduce((orders, user) => {
    return orders.concat(
        arrayLoop(5).map(() => {
            const status = 'Open';
            const date = moment().format('YYYY/MM/DD HH:mm:ss');
            return randomOrder({ user, status, date });
        })
    );
}, []);

const seed = [...randomUsersOrders, ...localUsersOrders, ...testUsersOrders];
export const Order = catalog('orders', seed);

export const list = ({ page = 1, size = 10, mode = 'user', ...filters }) => {
    return new Promise((resolve) => {
        const { date: dateFilter, user: userFilter, status: statusFilter } = filters;
        const user = Auth.getUser();
        const orders = Order.all().sort((orderA, orderB) => (orderA.date < orderB.date ? 1 : -1));
        let query = orders;
        if (mode == 'user' || user.role !== 'admin') {
            query = query.filter((o) => o.user == user.id);
        }
        if (dateFilter) {
            let filter = dateFilter.replace('-', '/');
            query = query.filter((o) => o.date.includes(filter));
        }
        if (userFilter) {
            const filter = userFilter.toLowerCase();
            const users = User.all()
                .filter((u) => u.name.toLowerCase().includes(filter))
                .map((u) => u.id);
            query = query.filter((o) => users.includes(o.user));
        }
        if (statusFilter) {
            let filter = statusFilter.toLowerCase();
            query = query.filter((o) => o.status.toLowerCase().includes(filter));
        }
        const start = (page - 1) * size;
        const end = start + size;
        const total = query.length;
        const records = query.slice(start, end).map((order) => {
            return {
                ...order,
                user: typeof order.user == 'string' ? User.find(order.user) : order.user,
                items: order.items.map((item) => {
                    return {
                        ...item,
                        product:
                            typeof item.product == 'string'
                                ? Product.find(item.product)
                                : item.product
                    };
                })
            };
        });
        setTimeout(() => {
            resolve({
                success: true,
                data: {
                    page,
                    size,
                    total,
                    records
                }
            });
        }, API_TIMEOUT);
    });
};

export const get = (id) => {
    return new Promise((resolve) => {
        const order = Order.find(id);
        setTimeout(() => {
            resolve({
                success: true,
                data: {
                    record: {
                        ...order,
                        user: User.find(order.user),
                        items: order.items.map((item) => {
                            return {
                                ...item,
                                product: Product.find(item.product)
                            };
                        })
                    }
                }
            });
        }, API_TIMEOUT);
    });
};

export const create = ({ user: orderUser, status: orderStatus, date: orderDate, items = [] }) => {
    return new Promise((resolve) => {
        const loggedUser = Auth.getUser();
        const date = loggedUser.role != 'admin' || !orderDate ? new Date() : orderDate;
        const order = {
            id: objectId(),
            date: moment(date).format('YYYY/MM/DD'),
            user: loggedUser.role != 'admin' || !orderUser ? loggedUser.id : orderUser,
            status: loggedUser.role != 'admin' || !orderStatus ? 'Open' : orderStatus,
            items
        };
        Order.add(order);
        setTimeout(() => {
            resolve({
                success: true,
                message: 'Order created successfully.',
                data: {
                    record: order
                }
            });
        }, API_TIMEOUT);
    });
};

export const update = (order) => {
    return new Promise((resolve) => {
        order.date = moment(order.date).format('YYYY/MM/DD');
        Order.update(order.id, order);
        setTimeout(() => {
            resolve({
                success: true,
                message: 'Order updated successfully.'
            });
        }, API_TIMEOUT);
    });
};

export const pay = (id) => {
    return new Promise((resolve) => {
        const order = Order.find(id);
        order.status = 'Paid';
        Order.update(id, order);
        setTimeout(() => {
            resolve({
                success: true,
                message: 'Order paid successfully.'
            });
        }, API_TIMEOUT);
    });
};

export const close = (id) => {
    return new Promise((resolve) => {
        const order = Order.find(id);
        order.status = 'Closed';
        Order.update(id, order);
        setTimeout(() => {
            resolve({
                success: true,
                message: 'Order closed successfully.'
            });
        }, API_TIMEOUT);
    });
};

export const cancel = (id) => {
    return new Promise((resolve) => {
        const order = Order.find(id);
        order.status = 'Cancelled';
        Order.update(id, order);
        setTimeout(() => {
            resolve({
                success: true,
                message: 'Order cancelled successfully.'
            });
        }, API_TIMEOUT);
    });
};

export const remove = (id) => {
    return new Promise((resolve) => {
        Order.remove(id);
        setTimeout(() => {
            resolve({
                success: true,
                message: 'Order deleted successfully.'
            });
        }, API_TIMEOUT);
    });
};
